import { getAccessToken } from "./supabase"

import type Search from "../types/search"

// ─── Config ──────────────────────────────────────────────────────────────────

/** HTTP header for frontapp → backapp service-to-service auth. */
export const BACK_API_KEY_HEADER = "X-Back-Api-Key"

export interface FastapiConfig {
  backapiUrl?: string
  domain?: string
  environment?: string
  platform?: string
}

/** Headers carrying `BACK_API_KEY` for server-side calls to backapp. */
export function buildBackApiKeyHeaders(key?: string): Record<string, string> {
  const resolved = (key ?? getBackApiKey())?.trim()
  if (!resolved) {
    throw new Error("BACK_API_KEY is not configured")
  }
  return { [BACK_API_KEY_HEADER]: resolved }
}

/** When PLATFORM=local but the bundle runs on a deployed hostname, use remote URLs. */
function resolvePlatformForRemoteHost(
  platform: string | undefined,
  domain: string | undefined,
): string | undefined {
  if (platform !== "local" || !domain || typeof window === "undefined") {
    return platform
  }
  const host = window.location?.hostname
  if (!host) {
    return platform
  }
  if (host === "localhost" || host.endsWith(".localhost")) {
    return platform
  }
  if (host === domain || host.endsWith(`.${domain}`)) {
    return "desktop"
  }
  return platform
}

/** Worktree slot (`SUBDOMAIN_SUFFIX=0` → `-0`) — dev/staging only, never production store builds. */
export function worktreeHostSuffix(environment?: string): string {
  if ((environment || "").trim().toLowerCase() === "production") {
    return ""
  }
  const raw =
    process.env.NEXT_PUBLIC_SUBDOMAIN_SUFFIX ??
    process.env.EXPO_PUBLIC_SUBDOMAIN_SUFFIX ??
    ""
  return raw ? `-${raw}` : ""
}

export function buildBackapiUrl(config: FastapiConfig): string {
  const { backapiUrl, domain, environment, platform } = config
  if (backapiUrl) return backapiUrl

  // Expo inlines EXPO_PUBLIC_*; Next inlines NEXT_PUBLIC_*. Accept both so
  // consumer-mobile (and shared packages) resolve the worktree suffix (-0, …).
  const backPrefix =
    process.env.NEXT_PUBLIC_BACK_PREFIX ??
    process.env.EXPO_PUBLIC_BACK_PREFIX ??
    "api"
  const suffix = worktreeHostSuffix(environment)
  const resolvedPlatform = resolvePlatformForRemoteHost(platform, domain)
  const env = (environment || "").trim().toLowerCase()

  // Production API is always api.<domain> — ignore worktree suffix and never localhost.
  if (env === "production") {
    return `https://${backPrefix}.${domain}/webapp`
  }
  if (resolvedPlatform === "local") return `http://${backPrefix}.localhost:83/webapp`
  return `https://${backPrefix}-${environment}${suffix}.${domain}/webapp`
}

export function buildOpsapiUrl(config: FastapiConfig): string {
  const { domain, environment, platform } = config
  const opsapiUrl =
    process.env.NEXT_PUBLIC_OPSAPI_URL ?? process.env.OPSAPI_URL
  if (opsapiUrl) return opsapiUrl.replace(/\/$/, "")

  const opsPrefix =
    process.env.NEXT_PUBLIC_OPS_PREFIX ??
    process.env.OPS_PREFIX ??
    "ops"
  // Ops hub is not slotted like product stacks (api-development-0); omit worktree suffix unless set.
  const opsSubdomainSuffix =
    process.env.NEXT_PUBLIC_OPS_SUBDOMAIN_SUFFIX ??
    process.env.OPS_SUBDOMAIN_SUFFIX ??
    ""
  const suffix = opsSubdomainSuffix ? `-${opsSubdomainSuffix}` : ""

  if (platform === "local") return `http://${opsPrefix}.localhost:83/webapp`
  if (environment === "production") return `https://${opsPrefix}${suffix}.${domain}/webapp`
  return `https://${opsPrefix}-${environment}${suffix}.${domain}/webapp`
}

/** Join a path (+ optional query) onto the URL set by `initBackapiUrl`. */
export function joinBackapiPath(path: string, query?: URLSearchParams): string {
  return _buildApiUrl(path, query ?? new URLSearchParams())
}

// ─── Module-level URL (populated by initBackapiUrl) ─────────────────────────

let _backapiUrl = ""

/** Server-only shared key for frontapp → backapp (never `NEXT_PUBLIC_` / `EXPO_PUBLIC_`). */
export function getBackApiKey(): string | undefined {
  // Dynamic key access — Next/Turbopack must not inline an empty value at compile time.
  const key = process.env["BACK_API_KEY"]?.trim()
  return key || undefined
}

export function getBackapiUrl(): string {
  return _backapiUrl
}

export function initBackapiUrl(url: string): void {
  _backapiUrl = url
}

/**
 * Resolve `BACK_API_KEY` for Next.js Route Handlers.
 * Returns a ready-to-return **503** `Response` when the key is missing (no Next.js import).
 */
export function requireBackApiKey():
  | { headers: Record<string, string>; key: string; ok: true }
  | { ok: false; response: Response } {
  const key = getBackApiKey()
  if (!key) {
    return {
      ok: false,
      response: Response.json(
        { detail: "BACK_API_KEY is not configured" },
        { status: 503 },
      ),
    }
  }
  return { headers: buildBackApiKeyHeaders(key), key, ok: true }
}

export interface FastapiRequestOptions {
  /** Extra query-string params merged into the request URL. */
  query?: Record<string, string>
  /** When true, do not require a Bearer token (optional Authorization if present). */
  skipAuth?: boolean
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

export async function deleteEntity<T>(
  path: string,
  id: string,
  options?: FastapiRequestOptions,
): Promise<T> {
  const query = new URLSearchParams()
  _appendQuery(query, options?.query)

  const url = _buildApiUrl(`${path}/${id}`, query)
  const response = await fetch(url, {
    method: "DELETE",
    headers: await _buildAuthHeaders(options?.skipAuth === true),
  })

  if (!response.ok) {
    const err = new Error(
      response.statusText || "Erreur lors du chargement",
    ) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}

export async function getEntities<T>(
  path: string,
  search: Search,
  select?: string | string[],
): Promise<T[]> {
  const token = await getAccessToken()
  if (!token) throw new Error("No active session")

  const { ascending, filter, key, limit, offset, order, value } = search
  const query = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  })

  if (value && value.trim()) query.set("value", value.trim())
  if (filter) {
    for (const [k, v] of Object.entries(filter)) {
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        query.set(k, String(v))
      }
    }
  }

  if (order) {
    query.set("order", order)
    if (ascending) query.set("ascending", String(ascending))
  }

  if (key) query.set("key", Array.isArray(key) ? key.join(",") : key)
  if (select) query.set("select", Array.isArray(select) ? select.join(",") : select)

  const url = _buildApiUrl(path, query)
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (!response.ok) {
    const detail = await messageFromFastapiErrorResponse(
      response,
      response.statusText || "Erreur lors du chargement",
    )
    const err = new Error(detail) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}

export async function getEntity<T>(
  path: string,
  id: string,
  select?: string | string[],
): Promise<T> {
  const token = await getAccessToken()
  if (!token) throw new Error("No active session")

  const query = new URLSearchParams()
  if (select) query.set("select", Array.isArray(select) ? select.join(",") : select)

  const url = _buildApiUrl(`${path}/${id}`, query)
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (!response.ok) {
    const err = new Error(
      response.statusText || "Erreur lors du chargement",
    ) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}

function detailFromFastapiErrorBody(body: unknown): string | null {
  if (typeof body !== "object" || body === null || !("detail" in body)) {
    return null
  }

  const detail = (body as { detail: unknown }).detail
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim()
  }

  if (!Array.isArray(detail)) {
    return null
  }

  const messages = detail
    .map((item) => {
      if (typeof item === "string" && item.trim()) return item.trim()
      if (item && typeof item === "object" && "msg" in item) {
        const msg = (item as { msg: unknown }).msg
        if (typeof msg === "string" && msg.trim()) return msg.trim()
      }
      return null
    })
    .filter((message): message is string => Boolean(message))

  return messages.length > 0 ? messages.join(" · ") : null
}

export async function messageFromFastapiErrorResponse(
  response: Response,
  fallback: string,
): Promise<string> {
  const status = response.status

  try {
    const raw = await response.text()
    if (!raw.trim()) {
      return status > 0 ? `${fallback} (HTTP ${status})` : fallback
    }

    try {
      const parsed = JSON.parse(raw) as unknown
      const detail = detailFromFastapiErrorBody(parsed)
      if (detail) return detail
    } catch {
      const snippet = raw.replace(/\s+/g, " ").trim().slice(0, 200)
      if (snippet) return snippet
    }
  } catch {
    /* keep fallback */
  }

  return status > 0 ? `${fallback} (HTTP ${status})` : fallback
}

export async function patchEntity<T>(
  path: string,
  id: string,
  body: T,
  select?: string | string[],
  options?: FastapiRequestOptions,
): Promise<T> {
  const query = new URLSearchParams()
  if (select) query.set("select", Array.isArray(select) ? select.join(",") : select)
  _appendQuery(query, options?.query)

  const url = _buildApiUrl(`${path}/${id}`, query)
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(await _buildAuthHeaders(options?.skipAuth === true)),
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const err = new Error(
      response.statusText || "Erreur lors du chargement",
    ) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}

export async function postEntity<T>(
  path: string,
  body: T,
  select?: string | string[],
  options?: FastapiRequestOptions,
): Promise<T> {
  const query = new URLSearchParams()
  if (select) query.set("select", Array.isArray(select) ? select.join(",") : select)
  _appendQuery(query, options?.query)

  const url = _buildApiUrl(path, query)
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(await _buildAuthHeaders(options?.skipAuth === true)),
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const detail = await messageFromFastapiErrorResponse(
      response,
      response.statusText || "Erreur lors de l'envoi",
    )
    const err = new Error(detail) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}

export async function postFormData<T>(path: string, form: FormData): Promise<T> {
  const token = await getAccessToken()
  if (!token) throw new Error("No active session")

  const url = _buildApiUrl(path, new URLSearchParams())
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  })

  if (!response.ok) {
    const err = new Error(
      response.statusText || "Erreur lors du chargement",
    ) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}

export async function putEntity<T>(
  path: string,
  body: T,
  select?: string | string[],
): Promise<T> {
  const token = await getAccessToken()
  if (!token) throw new Error("No active session")

  const query = new URLSearchParams()
  if (select) query.set("select", Array.isArray(select) ? select.join(",") : select)

  const url = _buildApiUrl(path, query)
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const err = new Error(
      response.statusText || "Erreur lors du chargement",
    ) as Error & { status: number }
    err.status = response.status
    throw err
  }

  return response.json()
}

// ─────────────────────────────────────────────────────────────────────────────
// # PRIVATE HELPERS
// ─────────────────────────────────────────────────────────────────────────────

async function _buildAuthHeaders(skipAuth = false): Promise<HeadersInit> {
  const token = await getAccessToken()
  if (!token && !skipAuth) throw new Error("No active session")
  const headers: HeadersInit = {}
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

function _appendQuery(params: URLSearchParams, query?: Record<string, string>): void {
  if (!query) return
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, value)
    }
  }
}

function _buildApiUrl(path: string, query: URLSearchParams): string {
  const pathNormalized = path.replace(/^\//, "").replace(/\/$/, "")
  const queryString = query.toString()
  let url = `${_backapiUrl.replace(/\/$/, "")}/${pathNormalized}`
  if (queryString) {
    url += pathNormalized.includes("?") ? `&${queryString}` : `?${queryString}`
  }
  return url
}
